import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useTenant } from '../../context/TenantContext';
import { getTenantLead, updateTenantLead } from '../../services/crmService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';

// DYNAMIC CUSTOM SERVICE MATRIX (Matches Lead Intake)
const CUSTOM_SERVICES = [
  { id: 'dirtToTurnkey', label: 'Dirt-To-Turnkey Deployments' },
  { id: 'bespokeInfra', label: 'Bespoke Digital Infrastructure' },
  { id: 'performanceConsulting', label: 'Performance & Sales Consulting' },
  { id: 'uiComponents', label: 'Customer UI Components & Widgets' },
  { id: 'decoupledWeb', label: 'De-Coupled Web Design' },
  { id: 'customSpa', label: 'Custom Single Page Applications' },
  { id: 'platformIntegration', label: 'Platform Deployment & Integration' },
  { id: 'customPos', label: 'Custom POS Services' },
  { id: 'other', label: 'Other / Custom Request' }
];

// --- SLIDE-OVER COMPONENT: EDIT PROFILE ---
function EditCustomerSlideOver({ customer, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: customer.name,
    status: customer.status,
    mrr: customer.financials.mrr,
    contactName: customer.contact.name,
    contactTitle: customer.contact.title,
    contactEmail: customer.contact.email,
    contactPhone: customer.contact.phone,
    entityRole: customer.details.entityRole || '',
    industry: customer.details.industry || '',
    employeeCount: customer.details.employeeCount || '',
    notes: customer.details.notes || ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedCustomer = {
      ...customer,
      name: formData.name,
      status: formData.status,
      financials: {
        ...customer.financials,
        mrr: parseFloat(formData.mrr) || 0,
      },
      contact: {
        ...customer.contact,
        name: formData.contactName,
        title: formData.contactTitle,
        email: formData.contactEmail,
        phone: formData.contactPhone,
      },
      details: {
        ...customer.details,
        entityRole: formData.entityRole,
        industry: formData.industry,
        employeeCount: formData.employeeCount,
        notes: formData.notes
      }
    };
    onSave(updatedCustomer);
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 animate-in fade-in duration-300" onClick={onClose}></div>
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-slate-50 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-300">
        <div className="bg-slate-900 p-6 flex justify-between items-center border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-lg font-black text-white uppercase tracking-widest">Edit Tenant Dossier</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">ID: {customer.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <form id="edit-customer-form" onSubmit={handleSubmit} className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Business Details</h3>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Entity Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Entity Role / Classification</label>
                  <select name="entityRole" value={formData.entityRole} onChange={handleChange} className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 cursor-pointer">
                    <option value="">Select Role...</option>
                    <option value="Authorized Retailer">Authorized Retailer</option>
                    <option value="Master Agent">Master Agent / Dealer</option>
                    <option value="Independent Operator">Independent Operator</option>
                    <option value="Direct Brand">Direct Brand</option>
                    <option value="Vendor / Partner">Vendor / Partner</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Industry</label>
                  <input type="text" name="industry" value={formData.industry} onChange={handleChange} className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Est. Headcount</label>
                  <select name="employeeCount" value={formData.employeeCount} onChange={handleChange} className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 cursor-pointer">
                    <option value="">Select Range...</option>
                    <option value="1-10">1 - 10</option>
                    <option value="11-50">11 - 50</option>
                    <option value="51-250">51 - 250 (Enterprise)</option>
                    <option value="250+">250+ (Global)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Status & Financials</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Pipeline Status</label>
                  <select name="status" value={formData.status} onChange={handleChange} className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 cursor-pointer">
                    <option value="New Lead">New Lead</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Negotiating">Negotiating</option>
                    <option value="Deal Desk Review">Deal Desk Review</option>
                    <option value="Active Tenant">Active Tenant</option>
                    <option value="Onboarding">Onboarding</option>
                    <option value="Paused">Paused</option>
                    <option value="Churned">Churned</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Target MRR ($)</label>
                  <input type="number" name="mrr" value={formData.mrr} onChange={handleChange} required className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Primary Contact</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Full Name</label>
                  <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} required className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Job Title</label>
                  <input type="text" name="contactTitle" value={formData.contactTitle} onChange={handleChange} required className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Email Address</label>
                <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} required className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Direct Phone</label>
                <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleChange} required className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2">Operational Notes</h3>
              <div>
                <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 resize-none"></textarea>
              </div>
            </div>
          </form>
        </div>

        <div className="p-6 bg-white border-t border-slate-300 flex gap-4 shrink-0">
          <button type="button" onClick={onClose} className="flex-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-[10px] font-bold py-3 uppercase tracking-widest transition-colors">
            Cancel
          </button>
          <button type="submit" form="edit-customer-form" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700 shadow-sm text-[10px] font-bold py-3 uppercase tracking-widest transition-colors">
            Save Profile
          </button>
        </div>
      </div>
    </>
  );
}

// --- SLIDE-OVER COMPONENT: LOG INTERACTION ---
function LogInteractionSlideOver({ onClose, onSave }) {
  const [formData, setFormData] = useState({
    type: 'Phone Call',
    details: '',
    followUpDate: '',
    followUpTime: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 animate-in fade-in duration-300" onClick={onClose}></div>
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-slate-50 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-300">
        <div className="bg-slate-900 p-6 flex justify-between items-center border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-lg font-black text-white uppercase tracking-widest">Log Interaction</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Record activity & set follow up</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <form id="log-interaction-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Interaction Type</label>
                <select name="type" value={formData.type} onChange={handleChange} required className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 cursor-pointer">
                  <option value="Phone Call">Phone Call</option>
                  <option value="Email">Email</option>
                  <option value="Text Message">Text Message</option>
                  <option value="Virtual Meeting">Virtual Meeting</option>
                  <option value="In Person Meeting">In Person Meeting</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Interaction Details & Notes</label>
                <textarea name="details" value={formData.details} onChange={handleChange} required rows="5" placeholder="What was discussed?" className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 resize-none"></textarea>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-200">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Schedule Next Action (Syncs to Calendar)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Follow Up Date</label>
                  <input type="date" name="followUpDate" value={formData.followUpDate} onChange={handleChange} className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Time</label>
                  <input type="time" name="followUpTime" value={formData.followUpTime} onChange={handleChange} className="w-full bg-white border border-slate-300 text-slate-900 text-sm font-bold px-3 py-2 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" />
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="p-6 bg-white border-t border-slate-300 flex gap-4 shrink-0">
          <button type="button" onClick={onClose} className="flex-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-[10px] font-bold py-3 uppercase tracking-widest transition-colors">
            Cancel
          </button>
          <button type="submit" form="log-interaction-form" className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 shadow-sm text-[10px] font-bold py-3 uppercase tracking-widest transition-colors">
            Log Activity
          </button>
        </div>
      </div>
    </>
  );
}

// --- MODAL: INVOICE GENERATOR & PAYMENT LINK DISPATCH ---
function InvoiceActionModal({ isOpen, onClose, customer, setupFee, mrrFee, onGenerate, checkoutUrl, isGenerating }) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    if (!checkoutUrl) return;
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handlePayNow = () => {
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95">
        <div className="bg-slate-900 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-black text-white uppercase tracking-tight">Generate Invoice & Payment</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{customer.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <div className="p-6 bg-slate-50 border-b border-slate-200">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">One-Time Deposit / Setup</span>
            <span className="text-base font-black text-slate-900">${setupFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Monthly Recurring (MRR)</span>
            <span className="text-base font-black text-emerald-600">${mrrFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}/mo</span>
          </div>
        </div>

        <div className="p-6 space-y-4 bg-white">
          {!checkoutUrl ? (
            <div className="text-center py-4">
              <p className="text-xs text-slate-600 mb-6 font-medium">
                Click below to initialize the cryptographic Stripe checkout session for <strong>{customer.contact.email}</strong>.
              </p>
              <button
                onClick={onGenerate}
                disabled={isGenerating}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold py-3.5 px-6 uppercase tracking-widest shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isGenerating ? <><i className="fa-solid fa-spinner fa-spin"></i> Generating Secure Link...</> : <><i className="fa-brands fa-stripe text-lg"></i> Generate Stripe Checkout Session</>}
              </button>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in">
              <div className="p-3 bg-emerald-50 border border-emerald-200 flex items-center gap-2 text-emerald-700 text-xs font-bold">
                <i className="fa-solid fa-circle-check text-emerald-600"></i> Session Ready. Select your payment collection method:
              </div>

              <button
                onClick={handlePayNow}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold py-3.5 px-4 uppercase tracking-widest transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <i className="fa-solid fa-credit-card"></i> Charge Card Now (Phone Call)
              </button>

              <button
                onClick={handleCopy}
                className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 text-[11px] font-bold py-3 px-4 uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
              >
                <i className={copied ? "fa-solid fa-check text-emerald-600" : "fa-solid fa-link"}></i>
                {copied ? "Payment Link Copied to Clipboard!" : "Copy Payment Link (Email / Chat)"}
              </button>
            </div>
          )}
        </div>

        <div className="p-4 bg-slate-100 border-t border-slate-200 text-right">
          <button
            onClick={onClose}
            className="text-[10px] font-bold text-slate-500 hover:text-slate-800 uppercase tracking-widest"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// --- MAIN PROFILE COMPONENT ---
export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantId, loading: tenantLoading } = useTenant(); 
  
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  
  const [showEditForm, setShowEditForm] = useState(false);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [generatedCheckoutUrl, setGeneratedCheckoutUrl] = useState(null);
  const [customer, setCustomer] = useState(null);

  // Replaces the hardcoded Web Design / Bespoke states with dynamic array tracking
  const [customPrices, setCustomPrices] = useState(
    CUSTOM_SERVICES.reduce((acc, s) => ({ ...acc, [s.id]: '' }), {})
  );

  const [appStates, setAppStates] = useState({});

  const [upgradeFlow, setUpgradeFlow] = useState({
    isOpen: false,
    appKey: null,
    billingChoice: 'prorated',
    isProcessing: false
  });

  const [downgradeFlow, setDowngradeFlow] = useState({
    isOpen: false,
    appKey: null,
    isProcessing: false
  });

  // --- ⚡️ LIVE FIREBASE FETCH (MULTI-TENANT) ⚡️ ---
  useEffect(() => {
    const fetchCustomer = async () => {
      if (tenantLoading || !tenantId || !id) return;
      
      setIsLoading(true);
      try {
        const data = await getTenantLead(tenantId, id);

        if (data) {
          const isApex = data.packageType === 'apex_os';
          const isOps = data.packageType === 'operations';
          const isHosting = data.services?.managedHosting || data.packageType === 'managed_hosting';
          const isCrm = data.services?.crm || data.assigned_apps?.eekoCrm; 
          
          // DYNAMIC CRM PRICING: If Apex is requested/active, CRM is $99. Else $249.
          const initialCrmPrice = isApex ? 99 : 249;

          setAppStates({
            apex_os: { 
              status: isApex ? 'active' : 'inactive', 
              price: 249, 
              name: 'Apex-OS Suite', 
              icon: 'fa-rocket' 
            },
            operations: { 
              status: isOps ? 'active' : 'inactive', 
              price: 149, 
              name: 'Operations Hub', 
              icon: 'fa-layer-group' 
            },
            crm: { 
              status: isCrm ? 'active' : 'inactive', 
              price: initialCrmPrice, 
              name: 'eeKoSystem CRM', 
              icon: 'fa-users-viewfinder' 
            },
            hosting: { 
              status: isHosting ? 'active' : 'inactive', 
              price: 149, 
              name: 'Managed Hosting', 
              icon: 'fa-server' 
            }
          });

          const personalName = data.firstName ? `${data.firstName} ${data.lastName || ''}`.trim() : null;
          let displayStatus = data.status || data.stage;
          if (data.stage === 'closedWon' && data.status !== 'Deal Desk Review') displayStatus = 'Active Tenant';
          else if (data.stage === 'newLeads') displayStatus = 'New Lead';

          setCustomer({
            id: data.id,
            name: data.company || data.entityName || data.company_name || personalName || 'Unknown Entity',
            status: displayStatus,
            details: {
              entityRole: data.entity_role || data.entityRole || '',
              industry: data.industry || '',
              employeeCount: data.employee_count || data.employeeCount || '',
              notes: data.notes || ''
            },
            documents: data.documents || {},
            financials: {
              mrr: Number(data.mrr_value || data.mrr || 0),
              lifetimeSpend: Number(data.amountPaid || data.calculatedOneTime || data.totalDueToday || 0),
              paymentMethod: (data.authProvisioned || data.paymentStatus === 'paid') ? "Stripe Checkout" : "Awaiting Setup",
              paymentStatus: (data.authProvisioned || data.paymentStatus === 'paid') ? "Current" : "Pending Action",
              stripeCustomerId: data.stripePaymentIntentId || "cus_pending",
              stripeSubscriptionId: "sub_pending"
            },
            dates: {
              startDate: data.createdAt?.toDate ? data.createdAt.toDate().toLocaleDateString() : 'N/A',
              renewalDate: 'TBD',
              lastFollowUp: data.lastTouch?.toDate ? data.lastTouch.toDate().toLocaleDateString() : 'Just Now',
              appointment: data.appointmentDate ? `${data.appointmentDate} @ ${data.appointmentTime}` : null
            },
            contact: {
              name: data.primaryContact || data.contact_name || data.contactName || personalName || 'Unassigned',
              title: data.contactTitle || 'Primary Contact',
              email: data.email || data.contactEmail || data.contact_email || 'N/A',
              phone: data.phone || data.contactPhone || data.contact_phone || 'N/A'
            },
            provisioning: {
              packageType: data.packageType || 'bespoke',
              services: data.services || {},
              assignedApps: data.assigned_apps || {} // Ingests from LeadIntakeForm
            }
          });
        } else {
          console.error("Deal not found in database.");
          setCustomer(null);
        }
      } catch (error) {
        console.error("Error fetching deal profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomer();
  }, [tenantId, tenantLoading, id]);

  // DYNAMIC BUNDLE LISTENER: Watch Apex Status and adjust CRM pricing instantly
  useEffect(() => {
    setAppStates(prev => {
      if (!prev.crm || !prev.apex_os) return prev;
      const isApexActive = prev.apex_os.status === 'active' || prev.apex_os.status === 'pending';
      const currentCrmPrice = isApexActive ? 99 : 249;
      
      if (prev.crm.price !== currentCrmPrice) {
        return { ...prev, crm: { ...prev.crm, price: currentCrmPrice } };
      }
      return prev;
    });
  }, [appStates.apex_os?.status]);

  const handleFileUpload = async (e, docKey) => {
    const file = e.target.files[0];
    if (!file || !tenantId || !customer) return;

    setIsUploadingDoc(true);
    try {
      const fileExtension = file.name.split('.').pop();
      const storagePath = `tenants/${tenantId}/deal_documents/${customer.id}/${docKey}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const updatedDocs = {
        ...(customer.documents || {}),
        [docKey]: downloadURL
      };

      await updateTenantLead(tenantId, customer.id, {
        documents: updatedDocs
      });

      setCustomer(prev => ({
        ...prev,
        documents: updatedDocs
      }));
    } catch (error) {
      console.error("Secure upload failed:", error);
      alert("System Error: Failed to vault the document. Check console.");
    } finally {
      setIsUploadingDoc(false);
      e.target.value = null; // Reset input
    }
  };

  if (isLoading || tenantLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh] flex-col">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Decrypting Tenant File...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh]">
        <i className="fa-solid fa-file-circle-xmark text-4xl text-slate-300 mb-4"></i>
        <h2 className="text-xl font-black text-slate-800 uppercase tracking-widest">Profile Not Found</h2>
        <p className="text-sm text-slate-500 mt-2 mb-6">The requested deal ID ({id}) could not be located in the database.</p>
        <button onClick={() => navigate(-1)} className="bg-slate-900 text-white px-6 py-2 text-[10px] font-bold uppercase tracking-widest">Return to Pipeline</button>
      </div>
    );
  }

  const mrrHistory = [
    { month: 'Nov', mrr: customer.financials.mrr * 0.5 },
    { month: 'Dec', mrr: customer.financials.mrr * 0.5 },
    { month: 'Jan', mrr: customer.financials.mrr * 0.8 }, 
    { month: 'Feb', mrr: customer.financials.mrr * 0.8 },
    { month: 'Mar', mrr: customer.financials.mrr }, 
    { month: 'Apr', mrr: customer.financials.mrr },
  ];

  const monogram = customer.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  // --- FIREBASE SYNC: SAVE PROFILE ---
  const handleSaveProfile = async (updatedData) => {
    if (!tenantId || !id) return;
    
    setCustomer(updatedData);
    setShowEditForm(false);

    try {
      let targetStage = updatedData.status;
      if (updatedData.status === 'Active Tenant') targetStage = 'closedWon';
      if (updatedData.status === 'New Lead') targetStage = 'newLeads';

      await updateTenantLead(tenantId, id, {
        company_name: updatedData.name,
        company: updatedData.name,
        mrr: updatedData.financials.mrr,
        mrr_value: updatedData.financials.mrr,
        stage: targetStage,
        contact_name: updatedData.contact.name,
        contactTitle: updatedData.contact.title,
        contactEmail: updatedData.contact.email,
        contactPhone: updatedData.contact.phone,
        entity_role: updatedData.details.entityRole,
        industry: updatedData.details.industry,
        employee_count: updatedData.details.employeeCount,
        notes: updatedData.details.notes
      });
    } catch (error) {
      console.error("Failed to sync customer profile to Firebase:", error);
    }
  };

  // --- FIREBASE SYNC: LOG INTERACTION & SCHEDULE ---
  const handleLogInteraction = async (interactionData) => {
    if (!tenantId || !id) return;
    setShowInteractionForm(false);

    try {
      const now = new Date();
      const dateString = now.toLocaleDateString();
      const newNoteEntry = `[${dateString} - ${interactionData.type}] ${interactionData.details}`;
      const updatedNotes = customer.details.notes ? `${newNoteEntry}\n\n${customer.details.notes}` : newNoteEntry;

      let scheduledDateIso = null;
      if (interactionData.followUpDate && interactionData.followUpTime) {
        const d = new Date(`${interactionData.followUpDate}T${interactionData.followUpTime}:00`);
        scheduledDateIso = d.toISOString();
      }

      const payload = {
        notes: updatedNotes,
        lastTouch: now
      };

      if (scheduledDateIso) {
        payload.scheduledDate = scheduledDateIso; 
        payload.appointmentDate = interactionData.followUpDate;
        payload.appointmentTime = interactionData.followUpTime;
      }

      await updateTenantLead(tenantId, id, payload);

      setCustomer(prev => ({
        ...prev,
        details: { ...prev.details, notes: updatedNotes },
        dates: { 
          ...prev.dates, 
          lastFollowUp: 'Just Now',
          appointment: scheduledDateIso ? `${interactionData.followUpDate} @ ${interactionData.followUpTime}` : prev.dates.appointment
        }
      }));

    } catch (error) {
      console.error("Failed to log interaction:", error);
      alert("Error logging interaction to database.");
    }
  };

  // Calculates the sum of all dynamic custom inputs
  const totalDepositAmount = Object.values(customPrices).reduce((total, val) => total + Number(val || 0), 0);

  // Dynamic MRR: Sum up any modules that are active or awaiting payment (pending)
  const activeCalculatedMrr = Object.values(appStates).reduce((total, app) => {
    if (app.status === 'active' || app.status === 'pending' || app.status === 'pending_downgrade') {
      return total + app.price;
    }
    return total;
  }, 0) || (customer.financials.mrr || 0);


  const handleExecuteGenerateInvoice = async () => {
    setIsGeneratingInvoice(true);
    try {
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

      const response = await createCheckoutSession({
        dealerId: customer.id,
        dealerName: customer.name,
        email: customer.contact.email,
        setupFee: totalDepositAmount, 
        mrr: activeCalculatedMrr,
        customItemName: 'Custom Web Design & Engineering',
        subscriptionItemName: 'Managed Hosting & Website Maintenance'
      });

      if (response.data?.url) {
        setGeneratedCheckoutUrl(response.data.url);
      } else {
        alert("Failed to generate secure checkout link.");
      }
    } catch (error) {
      console.error("Invoice Error:", error);
      alert("Failed to initialize billing session. Check console.");
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const isOutOfContract = () => {
    if (!customer?.dates?.renewalDate || customer.dates.renewalDate === 'TBD') return false;
    const renewal = new Date(customer.dates.renewalDate);
    const today = new Date();
    return today >= renewal; 
  };

  const handleAppToggle = (appKey) => {
    const app = appStates[appKey];
    if (appKey === 'apex_os' && app.status === 'active') return; 
    if (appKey === 'operations' && app.status === 'active') return;

    if (app.status === 'active') {
      if (!isOutOfContract()) {
        alert("Action Denied: Tenant is still within their initial 12-month contract. Early downgrades require Founder authorization.");
        return;
      }
      setDowngradeFlow({ isOpen: true, appKey, isProcessing: false });
      return;
    }

    if (app.status === 'pending_downgrade') {
      alert("This module is already scheduled for cancellation at the end of the billing cycle.");
      return;
    }

    if (app.status === 'pending') {
      // INVOICE BUILDER UX: If it is pending, let them toggle it off to remove it from the cart
      setAppStates(prev => ({ ...prev, [appKey]: { ...prev[appKey], status: 'inactive' } }));
      return;
    }
    
    // INVOICE BUILDER UX: If they are a new lead without a billing setup, bypass the upgrade modal!
    if (customer.financials.paymentStatus === 'Pending Action' || customer.financials.paymentMethod === 'Awaiting Setup') {
       setAppStates(prev => ({ ...prev, [appKey]: { ...prev[appKey], status: 'pending' } }));
       return;
    }

    setUpgradeFlow({ isOpen: true, appKey, billingChoice: 'prorated', isProcessing: false });
  };

  const executeUpgrade = () => {
    setUpgradeFlow(prev => ({ ...prev, isProcessing: true }));
    setTimeout(() => {
      const appKey = upgradeFlow.appKey;
      setAppStates(prev => ({ ...prev, [appKey]: { ...prev[appKey], status: 'pending' } }));
      setUpgradeFlow({ isOpen: false, appKey: null, billingChoice: 'prorated', isProcessing: false });
    }, 1500);
  };

  const executeDowngrade = () => {
    setDowngradeFlow(prev => ({ ...prev, isProcessing: true }));
    setTimeout(() => {
      const appKey = downgradeFlow.appKey;
      setAppStates(prev => ({ ...prev, [appKey]: { ...prev[appKey], status: 'pending_downgrade' } }));
      setDowngradeFlow({ isOpen: false, appKey: null, isProcessing: false });
    }, 1500);
  };

  const simulateStripeWebhook = (appKey) => {
    setAppStates(prev => ({ ...prev, [appKey]: { ...prev[appKey], status: 'active' } }));
    setCustomer(prev => ({
      ...prev,
      financials: { ...prev.financials, mrr: prev.financials.mrr + appStates[appKey].price }
    }));
  };

  // Reusable Document Row Component
  const renderDocRow = (docKey, label) => {
    const fileUrl = customer.documents?.[docKey];
    const isUploaded = !!fileUrl;

    return (
      <div key={docKey} className={`flex items-center justify-between p-3 border transition-colors ${isUploaded ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 flex items-center justify-center border ${isUploaded ? 'bg-emerald-100 border-emerald-200 text-emerald-600' : 'bg-white border-slate-200 text-slate-400'}`}>
            <i className={`fa-solid ${isUploaded ? 'fa-file-circle-check' : 'fa-file-pdf'}`}></i>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-900">{label}</p>
            {isUploaded ? (
              <p className="text-[8px] font-bold text-emerald-600 uppercase tracking-widest mt-0.5">Securely Vaulted</p>
            ) : (
              <p className="text-[8px] font-bold text-amber-500 uppercase tracking-widest mt-0.5">Awaiting Upload</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="bg-white border border-slate-300 hover:border-emerald-500 hover:text-emerald-600 text-slate-600 cursor-pointer text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm">
            <i className="fa-solid fa-cloud-arrow-up mr-1"></i> {isUploaded ? 'Replace' : 'Upload'}
            <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, docKey)} accept=".pdf,.png,.jpg,.jpeg" />
          </label>
          {isUploaded ? (
            <button onClick={() => window.open(fileUrl, '_blank')} className="bg-slate-900 hover:bg-slate-800 text-white text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm">
              <i className="fa-solid fa-eye mr-1"></i> View
            </button>
          ) : (
            <button disabled className="bg-slate-200 text-slate-400 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest opacity-50 cursor-not-allowed">
              <i className="fa-solid fa-eye mr-1"></i> View
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="animate-in fade-in duration-500 pb-12 relative">
      
      {/* Back Link */}
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="text-[10px] font-bold text-slate-400 hover:text-emerald-600 uppercase tracking-widest transition-colors flex items-center gap-2 w-fit">
          <i className="fa-solid fa-arrow-left"></i> Back to Pipeline
        </button>
      </div>

      {/* DEAL DESK HANDOFF BANNER */}
      {customer.status === 'Deal Desk Review' && (
        <div className="bg-amber-50 border border-amber-200 p-4 mb-6 flex items-start gap-4 shadow-sm animate-in slide-in-from-top-2">
          <div className="w-10 h-10 bg-white border border-amber-200 text-amber-600 rounded-full flex items-center justify-center shrink-0">
            <i className="fa-solid fa-file-signature text-lg"></i>
          </div>
          <div>
            <h4 className="text-xs font-black text-amber-900 uppercase tracking-widest">Deal Desk Handoff Required</h4>
            <p className="text-[10px] font-bold text-amber-700 mt-1 leading-relaxed">
              This lead has been successfully secured. To complete the Master Agent provisioning process, please upload their signed NDA and EIN verification letter in the <strong>Deal Desk Documents</strong> vault below. Once uploaded, Founder administration will review and execute the final minting.
            </p>
          </div>
        </div>
      )}

      {/* Header Profile Card */}
      <div className="bg-white border border-slate-300 shadow-sm p-6 mb-6 flex justify-between items-start">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center font-black text-2xl border border-slate-700 shadow-inner">
            {monogram}
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Tenant ID: {customer.id}</p>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">{customer.name}</h1>
              <span className={`border text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest ${
                customer.status === 'Active Tenant' ? 'border-emerald-500 text-emerald-600 bg-emerald-50' : 
                customer.status === 'Deal Desk Review' ? 'border-amber-500 text-amber-600 bg-amber-50' : 
                customer.status === 'Churned' ? 'border-red-500 text-red-600 bg-red-50' :
                customer.status === 'New Lead' ? 'border-blue-500 text-blue-600 bg-blue-50' :
                'border-slate-500 text-slate-600 bg-slate-50'
              }`}>
                {customer.status}
              </span>
            </div>
            <p className="text-xs font-bold text-slate-500 mt-2">
              {customer.details.entityRole || 'Unknown Role'} {customer.details.industry && `• ${customer.details.industry}`} {customer.details.employeeCount && `• ${customer.details.employeeCount} Employees`}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowEditForm(true)}
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-[10px] font-bold py-2 px-4 transition-colors uppercase tracking-widest"
          >
            Edit Profile
          </button>
          <button 
            onClick={() => setShowInteractionForm(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-2 px-4 transition-colors uppercase tracking-widest border border-slate-900 shadow-sm"
          >
            Log Interaction
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-emerald-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Current MRR</p>
          <p className="text-2xl font-black text-slate-800">${activeCalculatedMrr.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-cyan-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Est. Setup LTV</p>
          <p className="text-2xl font-black text-slate-800">${customer.financials.lifetimeSpend.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-amber-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
            {customer.dates.appointment ? "Next Scheduled Call" : "Origination Date"}
          </p>
          <p className="text-xl font-black text-slate-800 mt-1">
            {customer.dates.appointment || customer.dates.startDate}
          </p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-4 border-l-4 border-l-blue-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Last Interaction</p>
          <p className="text-xl font-black text-slate-800 mt-1">{customer.dates.lastFollowUp}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          
          {/* Recharts Area */}
          <div className="bg-slate-900 border border-slate-800 shadow-xl flex flex-col h-[280px]">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> Account MRR Expansion (6 Mo)
              </h3>
            </div>
            <div className="p-4 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mrrHistory} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorTenantMrr" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: '0px' }}
                    itemStyle={{ color: '#10B981', fontSize: '12px', fontWeight: 'bold' }}
                    labelStyle={{ color: '#64748B', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'MRR']}
                  />
                  <Area type="stepAfter" dataKey="mrr" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorTenantMrr)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-300 shadow-sm flex flex-col">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 flex justify-between items-center">
              <h2 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Active Provisioning Array</h2>
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Click inactive modules to trigger upgrade</span>
            </div>
            
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(appStates).map(([key, app]) => {
                let borderClass = 'border-slate-200';
                let bgClass = 'bg-white hover:border-emerald-300 hover:bg-slate-50 cursor-pointer';
                let iconClass = 'text-slate-300';
                let statusBadge = null;

                if (app.status === 'active') {
                  borderClass = 'border-emerald-500';
                  bgClass = 'bg-emerald-50/30 cursor-default';
                  iconClass = 'text-emerald-500';
                  statusBadge = <span className="text-[8px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-100 px-2 py-0.5"><i className="fa-solid fa-check"></i> Provisioned</span>;
                } else if (app.status === 'pending_downgrade') {
                  borderClass = 'border-red-400 border-dashed';
                  bgClass = 'bg-red-50 cursor-default';
                  iconClass = 'text-red-400';
                  statusBadge = <span className="text-[8px] font-black text-red-600 uppercase tracking-widest bg-red-100 px-2 py-0.5"><i className="fa-solid fa-calendar-xmark"></i> Cancels Next Cycle</span>;
                } else if (app.status === 'pending') {
                  borderClass = 'border-amber-400 border-dashed';
                  bgClass = 'bg-amber-50 cursor-default';
                  iconClass = 'text-amber-500 animate-pulse';
                  statusBadge = <span className="text-[8px] font-black text-amber-600 uppercase tracking-widest bg-amber-100 px-2 py-0.5"><i className="fa-solid fa-clock"></i> Awaiting Payment</span>;
                } else {
                  statusBadge = <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-2 py-0.5">Inactive</span>;
                }

                return (
                  <div key={key} onClick={() => handleAppToggle(key)} className={`border p-4 flex flex-col justify-between transition-all ${borderClass} ${bgClass}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-sm font-black text-slate-900">{app.name}</p>
                        <p className="text-[10px] font-bold text-slate-500 uppercase mt-0.5">+${app.price}/mo</p>
                      </div>
                      <i className={`fa-solid ${app.icon || 'fa-cube'} text-xl ${iconClass}`}></i>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      {statusBadge}
                      {app.status === 'pending' && (
                        <button onClick={(e) => { e.stopPropagation(); simulateStripeWebhook(key); }} className="text-[8px] text-amber-500 hover:text-emerald-500 underline">
                          Force Clear
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* DYNAMIC CUSTOM SAAS SERVICES MATRIX */}
            <div className="px-4 pb-4">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">Custom SaaS & Engineering Services (One-Time)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {CUSTOM_SERVICES.map((service) => {
                  const isRequested = customer.provisioning.assignedApps?.[service.id];
                  
                  return (
                    <div key={service.id} className="border border-slate-200 p-4 bg-white flex flex-col justify-between hover:border-emerald-500 transition-colors relative">
                      {isRequested && (
                        <div className="absolute -top-2 -right-2 bg-emerald-100 text-emerald-700 border border-emerald-300 text-[8px] font-black px-2 py-0.5 uppercase tracking-widest shadow-sm">
                          Client Requested
                        </div>
                      )}
                      <div>
                        <h3 className="text-sm font-black text-slate-900">{service.label}</h3>
                        <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-widest">Variable One-Time Fee</p>
                      </div>
                      <div className="mt-4 flex items-center gap-2">
                        <span className="text-slate-500 font-bold text-sm">$</span>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={customPrices[service.id]}
                          onChange={(e) => setCustomPrices({...customPrices, [service.id]: e.target.value})}
                          className="bg-slate-50 border border-slate-300 text-sm font-bold p-2 w-full focus:outline-none focus:border-emerald-500 text-slate-800"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
          
          {customer.details.notes && (
            <div className="bg-white border border-slate-300 shadow-sm">
              <div className="bg-slate-100 border-b border-slate-300 px-4 py-3">
                <h2 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Operational Notes</h2>
              </div>
              <div className="p-4">
                <p className="text-sm font-medium text-slate-700 whitespace-pre-wrap">{customer.details.notes}</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3">
              <h2 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Primary Contact</h2>
            </div>
            <div className="p-4">
              <p className="font-bold text-slate-900 text-lg">{customer.contact.name}</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">{customer.contact.title}</p>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Email</span>
                  <a href={`mailto:${customer.contact.email}`} className="text-sm font-bold text-emerald-600 hover:text-emerald-800 transition-colors flex items-center gap-2">
                    {customer.contact.email} <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                  </a>
                </div>
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Direct</span>
                  <a href={`tel:${customer.contact.phone.replace(/[^0-9]/g, '')}`} className="text-sm font-bold text-emerald-600 hover:text-emerald-800 transition-colors flex items-center gap-2">
                    {customer.contact.phone} <i className="fa-solid fa-phone text-[10px]"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* DEAL DESK DOCUMENTS VAULT */}
          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 flex justify-between items-center">
              <h2 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Deal Desk Documents</h2>
              {isUploadingDoc && <span className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest animate-pulse">Encrypting...</span>}
            </div>
            <div className="p-4 space-y-3">
              {renderDocRow('nda', 'Signed Non-Disclosure (NDA)')}
              {renderDocRow('ein', 'EIN / Tax ID Verification')}
              {renderDocRow('msa', 'Executed Master Agreement')}
            </div>
          </div>

          {/* TAX & B2B COMPLIANCE VAULT */}
          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex justify-between items-center">
              <h2 className="text-[10px] font-black text-amber-800 uppercase tracking-widest flex items-center gap-2">
                <i className="fa-solid fa-shield-halved"></i> B2B Compliance & Tax Vault
              </h2>
            </div>
            <div className="p-4 space-y-3">
              {renderDocRow('resaleCertificate', 'State Resale / Tax Exemption Certificate')}
              {renderDocRow('vendorW9', 'Vendor W-9 (If Applicable)')}
            </div>
          </div>

          <div className="bg-white border border-slate-300 shadow-sm">
             <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 flex justify-between items-center">
              <h2 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Billing Status</h2>
            </div>
             <div className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Method</span>
                  <span className="text-sm font-bold text-slate-700">{customer.financials.paymentMethod}</span>
                </div>
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Standing</span>
                  <span className={`border text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest ${
                    customer.financials.paymentStatus === 'Current' ? 'border-emerald-500 text-emerald-600 bg-emerald-50' : 'border-amber-500 text-amber-600 bg-amber-50'
                  }`}>
                    {customer.financials.paymentStatus}
                  </span>
                </div>

                {/* ADDED CONTRACT STATUS CHECK */}
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Contract Status</span>
                  <span className={`border text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest ${
                    isOutOfContract() ? 'border-slate-300 text-slate-600 bg-slate-50' : 'border-blue-500 text-blue-600 bg-blue-50'
                  }`}>
                    {isOutOfContract() ? 'Month-to-Month' : '12-Month Term'}
                  </span>
                </div>
             </div>
             <div className="px-4 pb-4 mt-2">
               <button 
                 onClick={() => { setGeneratedCheckoutUrl(null); setShowInvoiceModal(true); }}
                 className="w-full bg-slate-900 border border-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-3 transition-colors uppercase tracking-widest shadow-sm flex items-center justify-center gap-2"
               >
                 <i className="fa-solid fa-receipt"></i> Generate Invoice
               </button>
             </div>
          </div>
        </div>
      </div>

      {/* UPGRADE MODAL */}
      {upgradeFlow.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95">
            <div className="bg-slate-900 p-6 flex justify-between items-center">
              <h2 className="text-lg font-black text-white uppercase tracking-tight">Feature Upgrade Protocol</h2>
              <button onClick={() => setUpgradeFlow({...upgradeFlow, isOpen: false})} className="text-slate-400 hover:text-white">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="p-6 bg-slate-50 border-b border-slate-200 text-center">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Target Module</p>
              <h3 className="text-xl font-black text-slate-900">{appStates[upgradeFlow.appKey].name}</h3>
              <p className="text-lg font-bold text-emerald-600 mt-2">${appStates[upgradeFlow.appKey].price}<span className="text-xs text-slate-500 ml-1">/ mo</span></p>
            </div>
            <div className="p-6 space-y-4 bg-white">
              <p className="text-[10px] font-black text-slate-800 uppercase tracking-widest mb-2 border-b border-slate-100 pb-2">Select Billing Execution</p>
              <label className={`block border p-4 cursor-pointer transition-colors ${upgradeFlow.billingChoice === 'prorated' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex gap-3">
                  <input 
                    type="radio" name="billingChoice" value="prorated" 
                    checked={upgradeFlow.billingChoice === 'prorated'}
                    onChange={(e) => setUpgradeFlow({...upgradeFlow, billingChoice: e.target.value})}
                    className="mt-1 w-4 h-4 text-emerald-600"
                  />
                  <div>
                    <p className="text-xs font-black text-slate-900 uppercase">Provision Today (Prorated)</p>
                    <p className="text-[10px] text-slate-600 mt-1">Stripe will immediately charge the card on file for the prorated amount of the remaining cycle. Feature activates instantly upon payment clearance.</p>
                  </div>
                </div>
              </label>
              <label className={`block border p-4 cursor-pointer transition-colors ${upgradeFlow.billingChoice === 'next_cycle' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex gap-3">
                  <input 
                    type="radio" name="billingChoice" value="next_cycle" 
                    checked={upgradeFlow.billingChoice === 'next_cycle'}
                    onChange={(e) => setUpgradeFlow({...upgradeFlow, billingChoice: e.target.value})}
                    className="mt-1 w-4 h-4 text-emerald-600"
                  />
                  <div>
                    <p className="text-xs font-black text-slate-900 uppercase">Provision on Next Cycle</p>
                    <p className="text-[10px] text-slate-600 mt-1">Adds the module to the invoice generating on {customer.dates.renewalDate}. Feature remains locked in pending state until that invoice is paid.</p>
                  </div>
                </div>
              </label>
            </div>
            <div className="p-6 bg-slate-100 flex gap-3">
              <button 
                onClick={() => setUpgradeFlow({...upgradeFlow, isOpen: false})} 
                className="flex-1 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold py-3 uppercase tracking-widest hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={executeUpgrade} disabled={upgradeFlow.isProcessing}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold py-3 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50"
              >
                {upgradeFlow.isProcessing ? 'Contacting Stripe...' : 'Lock In Upgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DOWNGRADE MODAL */}
      {downgradeFlow.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95">
            <div className="bg-slate-900 p-6 flex justify-between items-center">
              <h2 className="text-lg font-black text-white uppercase tracking-tight">Schedule Cancellation</h2>
              <button onClick={() => setDowngradeFlow({...downgradeFlow, isOpen: false})} className="text-slate-400 hover:text-white">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            
            <div className="p-6 bg-red-50 border-b border-red-100 text-center">
              <div className="w-12 h-12 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fa-solid fa-arrow-trend-down text-xl"></i>
              </div>
              <h3 className="text-xl font-black text-slate-900">{appStates[downgradeFlow.appKey].name}</h3>
              <p className="text-sm font-bold text-slate-600 mt-1">Scheduled MRR Reduction: <span className="text-red-600">-${appStates[downgradeFlow.appKey].price}/mo</span></p>
            </div>

            <div className="p-6 bg-white text-center">
              <p className="text-sm text-slate-700 font-medium">
                The tenant is currently out of contract. This module will remain active until the end of their current billing cycle. 
                They will not be billed for this module on their next invoice.
              </p>
            </div>

            <div className="p-6 bg-slate-100 flex gap-3">
              <button 
                onClick={() => setDowngradeFlow({...downgradeFlow, isOpen: false})} 
                className="flex-1 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold py-3 uppercase tracking-widest hover:bg-slate-50 transition-colors"
              >
                Abort
              </button>
              <button 
                onClick={executeDowngrade} disabled={downgradeFlow.isProcessing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold py-3 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50"
              >
                {downgradeFlow.isProcessing ? 'Updating Stripe...' : 'Schedule Downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT PROFILE SLIDE-OVER */}
      {showEditForm && (
        <EditCustomerSlideOver 
          customer={customer} 
          onClose={() => setShowEditForm(false)} 
          onSave={handleSaveProfile} 
        />
      )}

      {/* LOG INTERACTION SLIDE-OVER */}
      {showInteractionForm && (
        <LogInteractionSlideOver 
          onClose={() => setShowInteractionForm(false)} 
          onSave={handleLogInteraction} 
        />
      )}

      {/* INVOICE & PAYMENT MODAL */}
      <InvoiceActionModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        customer={customer}
        setupFee={totalDepositAmount}
        mrrFee={activeCalculatedMrr}
        onGenerate={handleExecuteGenerateInvoice}
        checkoutUrl={generatedCheckoutUrl}
        isGenerating={isGeneratingInvoice}
      />
    </div>
  );
}